import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useRecoveryPasswordStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, RoutePaths } from '@shared/utils';
import { ErrorRecoveryModal, SuccessRecoveryModal } from './widgets';
import { Footer } from '@widgets/footer';
import { Input } from '@shared/widgets/inputs';
import { Button, Error, ErrorList } from '@shared/widgets';
import logoImg from '@assets/images/logo.png';
import bgImg from '@assets/images/auth/bg-02.png';

interface IFormData {
  userName: string;
  cardNumber: string;
}

export const RecoveryPage = () => {
  const { loading, error: recoveryError, recoverPassword } = useRecoveryPasswordStore();

  const [showErrorRecoveryModal, setShowErrorRecoveryModal] = useState<boolean>(false);

  const { back } = useRouteHook();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormData>({ defaultValues: { userName: '', cardNumber: '' } });

  useEffect(() => {
    if (loading || !recoveryError) return;
    setShowErrorRecoveryModal(true);
  }, [recoveryError, loading]);

  const sendForm = (data: IFormData) => recoverPassword(data);

  return (
    <>
      <div className='absolute top-[10px] left-[67px] z-[200] h-[50px] w-[50px] max-md:top-0 max-md:left-0 max-md:w-full max-md:bg-[#0f0f114d]'>
        <NavLink className='mx-auto flex h-[50px] w-[50px] items-center justify-center max-md:h-[44px]' to={RoutePaths.home.onNavigate()}>
          <img className='h-full w-full object-contain' src={logoImg} alt='logo' />
        </NavLink>
      </div>
      <div className='mx-auto flex h-[702px] max-w-[1440px] overflow-hidden max-md:relative max-md:z-1 max-md:h-auto max-md:w-full max-md:flex-col-reverse'>
        <div
          className={cn(
            'relative z-99 h-[702px] w-full bg-right bg-no-repeat pl-37.5 text-left',
            'max-md:h-auto max-md:bg-size-[auto_60%] max-md:bg-top-right max-md:pt-[100%] max-md:pl-0',
          )}
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <form
            className='max-md:relative max-md:w-full max-md:px-4 max-md:pt-5 max-md:pb-12 max-md:after:absolute max-md:after:top-[-25px] max-md:after:left-[-50%] max-md:after:z-[-1] max-md:after:h-[200px] max-md:after:w-[200vw] max-md:after:rotate-[-7deg] max-md:after:bg-[#fff]'
            onSubmit={handleSubmit(sendForm)}
          >
            <h2 className='mt-[103px] font-[Prata] text-[56px] text-[#272727] max-md:mt-0 max-md:text-center max-md:text-[28px]'>
              Password Recovery
            </h2>
            <p className='mt-2 mb-7.5 text-lg font-light text-[#4c4c4c] before:absolute before:left-0 before:mt-[15px] before:h-[1px] before:w-[125px] before:bg-[#000] max-md:text-center max-md:text-[#4c4c4c80] max-md:before:hidden'>
              Enter the world of Insignia
            </p>
            <div className='mt-2 flex items-center gap-2 max-md:mt-4 max-md:block'>
              <Controller
                name='userName'
                control={control}
                rules={{ required: 'User name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Input {...field} inputClassName='w-[292px]' label='Username' name='userName' />
                    <Error className='mt-8 max-md:hidden' text={error?.message} />
                  </>
                )}
              />
            </div>
            <Controller
              name='cardNumber'
              control={control}
              rules={{
                required: 'Card number is required',
                minLength: { value: 4, message: 'Please enter last four digit of your card number' },
                maxLength: { value: 4, message: 'Please enter last four digit of your card number' },
              }}
              render={({ field, fieldState: { error } }) => (
                <div className='mt-2 flex items-center gap-2 max-md:mt-4 max-md:block'>
                  <Input {...field} inputClassName='w-[292px]' label='Card Number' name='cardNumber' />
                  <Error className='!mt-8 max-md:hidden' text={error?.message} />
                </div>
              )}
            />
            <ErrorList className='mt-6 hidden max-md:flex' errors={[errors.userName?.message, errors.cardNumber?.message]} />
            <div className='mt-8 flex gap-4 max-md:mt-8'>
              <Button className='w-[138px] max-md:w-full' color='outlined' onClick={back}>
                Cancel
              </Button>
              <Button className='w-[138px] max-md:w-full' type='submit' isLoading={loading}>
                Next
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
      <SuccessRecoveryModal />
      <ErrorRecoveryModal isOpen={showErrorRecoveryModal} onClose={() => setShowErrorRecoveryModal(false)} />
    </>
  );
};
